import { useEffect, useRef, useState } from "react";
import Nav from "../components/Nav";
import WellnessCheckCard from "../components/WellnessCheckCard";
import CrisisSupportCard from "../components/CrisisSupportCard";
import ChatRecapBanner from "../components/ChatRecapBanner";
import { useAuth } from "../context/AuthContext";
import { api, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { Send, ImagePlus, X, LifeBuoy } from "lucide-react";

export default function Chat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [photoPath, setPhotoPath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const [showCheckin, setShowCheckin] = useState(false);
  const [locked, setLocked] = useState(false);
  const [crisis, setCrisis] = useState(null);
  const [safetyText, setSafetyText] = useState("");
  const [safetySending, setSafetySending] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const crisisRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        // Carry over an anonymous taster conversation into this account (once).
        const transcript = localStorage.getItem("zaf_try_transcript");
        if (transcript) {
          try {
            const msgs = JSON.parse(transcript);
            if (Array.isArray(msgs) && msgs.length) {
              await api.post("/chat/import", { messages: msgs });
            }
          } catch (e) {}
          localStorage.removeItem("zaf_try_transcript");
          localStorage.removeItem("zaf_try_session");
        }
        const [{ data: h }, { data: s }, { data: sub }] = await Promise.all([
          api.get("/chat/history"),
          api.get("/checkin/status"),
          api.get("/subscription/status"),
        ]);
        setMessages(h.messages);
        setStatus(s);
        setLocked(!sub.has_access);
        if (s.needs_checkin) setShowCheckin(true);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  useEffect(() => {
    if (crisis) crisisRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [crisis]);

  const openHelp = async () => {
    try {
      const { data } = await api.get("/crisis-resources");
      setCrisis(data);
    } catch (e) {
      setCrisis({ region: "", hotlines: [], note: "If you're in immediate danger, please call your local emergency number now." });
    }
  };

  const send = async () => {
    if (!text.trim() && !photoPath) return;
    setSending(true);
    const payloadText = text.trim();
    const payloadPhoto = photoPath;
    setText("");
    setPhotoPath("");
    // optimistic
    const optimistic = {
      id: `tmp-${Date.now()}`,
      role: "user",
      text: payloadText,
      photo_path: payloadPhoto,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    try {
      const { data } = await api.post("/chat", { text: payloadText, photo_path: payloadPhoto });
      setMessages((prev) => [...prev.filter((m) => m.id !== optimistic.id), data.user_message, data.zaf_message]);
      if (data.crisis?.detected) setCrisis(data.crisis);
    } catch (err) {
      if (err?.response?.status === 402) {
        setLocked(true);
        setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      } else {
        toast.error("Zaf couldn't hear that. Try again in a sec.");
        setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      }
    } finally {
      setSending(false);
    }
  };

  const subscribe = () => {
    const base = process.env.REACT_APP_STRIPE_PAYMENT_LINK;
    if (!base) {
      toast.error("Subscription link isn't set up yet.");
      return;
    }
    const url = `${base}?client_reference_id=${encodeURIComponent(user.id)}&prefilled_email=${encodeURIComponent(user.email)}`;
    window.location.href = url;
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const sendSafety = async () => {
    const t = safetyText.trim();
    if (!t) return;
    setSafetySending(true);
    setSafetyText("");
    const optimistic = { id: `tmp-${Date.now()}`, role: "user", text: t, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, optimistic]);
    try {
      const { data } = await api.post("/chat", { text: t });
      setMessages((prev) => [...prev.filter((m) => m.id !== optimistic.id), data.user_message, data.zaf_message]);
      if (data.crisis?.detected) setCrisis(data.crisis);
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      if (err?.response?.status === 402) {
        toast("Your everyday chat is paused — but if you're ever in a dark place, tell me and I'll stay right here with you.", { duration: 7000 });
      } else {
        toast.error("Try that again in a moment.");
      }
    } finally {
      setSafetySending(false);
    }
  };

  const uploadPhoto = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(data.path);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const suggestions = [
    "Just wanted to say hi",
    "I had a rough day",
    "Something good happened",
    "I can't sleep",
  ];

  return (
    <div className="min-h-screen bg-[#F4F7F6] flex flex-col">
      <Nav />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 md:px-6 py-6 flex flex-col gap-4">
        <div className="flex justify-end">
          <button
            data-testid="crisis-open-btn"
            onClick={openHelp}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F7B75] hover:text-[#2E9D96] bg-[#2E9D96]/[0.06] hover:bg-[#2E9D96]/10 border border-[#2E9D96]/25 rounded-full px-4 py-1.5 transition-colors"
          >
            <LifeBuoy size={15} /> Need help now
          </button>
        </div>

        {!locked && <ChatRecapBanner />}

        {showCheckin && (
          <div className="relative">
            <button
              onClick={() => setShowCheckin(false)}
              className="absolute top-4 right-4 z-10 text-slate-400 hover:text-slate-700"
              aria-label="Dismiss"
              data-testid="wellness-dismiss"
            >
              <X size={18} />
            </button>
            <WellnessCheckCard
              onDone={(data) => {
                setShowCheckin(false);
                if (data?.support?.show) setCrisis(data.support);
              }}
            />
          </div>
        )}

        {crisis && (
          <div ref={crisisRef}>
            <CrisisSupportCard resources={crisis} onDismiss={() => setCrisis(null)} />
          </div>
        )}

        {status?.missed && (
          <div className="rounded-2xl bg-[#DCEFEC]/40 border border-[#2E9D96]/30 px-5 py-3 text-sm text-slate-700" data-testid="missed-checkin-banner">
            It's been a while since you checked in. Your trusted contact will be notified soon if we don't hear from you.
          </div>
        )}

        <div
          ref={scrollRef}
          data-testid="chat-scroll"
          className="flex-1 rounded-3xl bg-white border border-slate-100 shadow-sm p-4 md:p-6 overflow-y-auto min-h-[420px] max-h-[65vh]"
        >
          {messages.length === 0 && !sending && (
            <EmptyState name={user?.first_name} onPick={(t) => setText(t)} suggestions={suggestions} />
          )}
          <div className="space-y-3">
            {messages.map((m) => (
              <Bubble key={m.id} m={m} />
            ))}
            {sending && (
              <div className="flex items-end gap-2 bubble-in">
                <ZafAvatar />
                <div className="bg-[#2E9D96] text-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-1">
                    <span className="typing-dot inline-block w-1.5 h-1.5 rounded-full bg-white/80" />
                    <span className="typing-dot inline-block w-1.5 h-1.5 rounded-full bg-white/80" />
                    <span className="typing-dot inline-block w-1.5 h-1.5 rounded-full bg-white/80" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {photoPath && (
          <div className="rounded-2xl bg-white border border-slate-100 p-3 flex items-center gap-3">
            <img src={fileUrl(photoPath)} alt="attachment" className="w-14 h-14 object-cover rounded-xl" />
            <span className="text-sm text-slate-600 flex-1">Photo ready to send</span>
            <button onClick={() => setPhotoPath("")} className="text-slate-400 hover:text-slate-700" data-testid="chat-remove-photo">
              <X size={18} />
            </button>
          </div>
        )}

        {locked ? (
          <div className="space-y-3">
            <Paywall onSubscribe={subscribe} />
            <div className="rounded-3xl bg-[#F4F7F6] border border-[#2E9D96]/25 p-4 md:p-5" data-testid="safety-reach">
              <p className="text-sm text-slate-600 leading-relaxed">
                <span className="font-semibold text-slate-800">Having a hard time right now?</span> This part is
                always free. Tell me what's going on — I'm still here.
              </p>
              <div className="mt-3 rounded-full bg-white border border-slate-200 shadow-sm flex items-center gap-2 px-2 py-2">
                <input
                  value={safetyText}
                  onChange={(e) => setSafetyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendSafety();
                    }
                  }}
                  placeholder="I'm not okay right now…"
                  className="flex-1 bg-transparent outline-none px-2 text-slate-800 placeholder:text-slate-400"
                  data-testid="safety-input"
                />
                <button
                  onClick={sendSafety}
                  disabled={safetySending || !safetyText.trim()}
                  className="w-11 h-11 rounded-full bg-[#2E9D96] hover:bg-[#1F7B75] disabled:opacity-40 text-white flex items-center justify-center transition-colors"
                  data-testid="safety-send-btn"
                  aria-label="Send"
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </div>
        ) : (
        <div className="rounded-full bg-white border border-slate-200 shadow-sm flex items-center gap-2 px-2 py-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])}
            data-testid="chat-file-input"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="w-10 h-10 rounded-full text-slate-500 hover:text-[#2E9D96] hover:bg-slate-100 flex items-center justify-center transition-colors disabled:opacity-40"
            data-testid="chat-attach-btn"
            aria-label="Attach photo"
          >
            <ImagePlus size={18} />
          </button>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            placeholder="Tell Zaf anything…"
            className="flex-1 bg-transparent outline-none px-2 text-slate-800 placeholder:text-slate-400"
            data-testid="chat-input"
          />
          <button
            onClick={send}
            disabled={sending || (!text.trim() && !photoPath)}
            className="w-11 h-11 rounded-full bg-[#2E9D96] hover:bg-[#1F7B75] disabled:opacity-40 text-white flex items-center justify-center transition-colors"
            data-testid="chat-send-btn"
            aria-label="Send"
          >
            <Send size={18} />
          </button>
        </div>
        )}
      </main>
    </div>
  );
}

function Paywall({ onSubscribe }) {
  return (
    <div className="rounded-3xl bg-white border border-[#2E9D96]/30 shadow-sm p-6 md:p-8 text-center" data-testid="chat-paywall">
      <div className="w-12 h-12 rounded-2xl bg-[#2E9D96] text-white flex items-center justify-center mx-auto mb-4 font-display text-xl">Z</div>
      <h3 className="font-display text-2xl text-slate-800">Your free 3 days are up.</h3>
      <p className="text-slate-600 mt-2 max-w-md mx-auto">
        I've loved talking with you. Keep me in your corner — chat anytime, day or night — for less than a coffee a week.
      </p>
      <div className="mt-4 flex items-baseline justify-center gap-1">
        <span className="font-display text-4xl text-[#2E9D96]">$6.49</span>
        <span className="text-slate-500 font-semibold">/ week</span>
      </div>
      <button
        onClick={onSubscribe}
        data-testid="paywall-subscribe-btn"
        className="mt-6 inline-flex items-center gap-2 bg-[#2E9D96] hover:bg-[#1F7B75] text-white font-semibold px-8 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-sm"
      >
        Keep talking to Zaf
      </button>
      <p className="mt-3 text-xs text-slate-400">Cancel anytime. No lock-in.</p>
    </div>
  );
}

function ZafAvatar() {
  return (
    <div className="w-8 h-8 rounded-full bg-[#2E9D96] text-white flex items-center justify-center text-xs font-semibold shrink-0">
      Z
    </div>
  );
}

function Bubble({ m }) {
  const isZaf = m.role === "zaf";
  return (
    <div className={`bubble-in flex items-end gap-2 ${isZaf ? "" : "justify-end"}`} data-testid={`msg-${m.role}`}>
      {isZaf && <ZafAvatar />}
      <div className={`max-w-[78%] rounded-2xl px-4 py-3 shadow-sm ${isZaf ? "bg-[#2E9D96] text-white rounded-bl-md" : "bg-[#CDE7E3] text-slate-800 rounded-br-md"}`}>
        {m.photo_path && (
          <img src={fileUrl(m.photo_path)} alt="attachment" className="mb-2 rounded-xl max-h-64 object-cover" />
        )}
        {m.text && <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{m.text}</p>}
      </div>
    </div>
  );
}

function EmptyState({ name, onPick, suggestions }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center py-10">
      <div className="w-14 h-14 rounded-full bg-[#2E9D96] text-white flex items-center justify-center font-display text-xl mb-4">Z</div>
      <h3 className="font-display text-2xl text-slate-800">Hey {name || "friend"}.</h3>
      <p className="text-slate-500 mt-1 max-w-md">I'm Zaf. Big day, small day, quiet day — whatever it is, I'm here.</p>
      <div className="mt-6 flex flex-wrap gap-2 justify-center">
        {suggestions.map((s) => (
          <button
            key={s}
            onClick={() => onPick(s)}
            className="text-sm px-4 py-2 rounded-full bg-[#CDE7E3] hover:bg-[#B9DBD5] text-slate-700 transition-colors"
            data-testid={`chat-suggestion-${s.slice(0, 10)}`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
